import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminDashboardLayout from '../../components/layout/AdminDashboardLayout';
import DataTable from '../../components/common/DataTable';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import usePageTitle from '../../hooks/usePageTitle';
import useSmartRefresh from '../../hooks/useSmartRefresh';

/**
 * Admin Invoices Page - View and manage all system invoices
 */
const AdminInvoicesPage = () => {
  // Set page title
  usePageTitle('Admin Invoices');

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Mock data fetching function
  const fetchInvoicesData = async () => {
    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1000));
    console.log('Admin invoices data refreshed');
  };

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchInvoicesData,
    'Invoices refreshed successfully',
  );

  // Mock invoices data (system-wide)
  const invoices = [
    {
      id: 'INV-2025-001',
      orderIds: ['ORD-2025-001'],
      customerName: 'John Doe',
      customerEmail: 'john.doe@example.com',
      customerPhone: '+1 (555) 123-4567',
      amount: 2.5,
      tax: 0.25,
      total: 2.75,
      status: 'paid',
      paymentMethod: 'Credit Card',
      issueDate: '2025-01-15',
      dueDate: '2025-01-30',
      paidDate: '2025-01-15',
      services: [{ name: 'IMEI Check', quantity: 1, price: 2.5 }],
    },
    {
      id: 'INV-2025-002',
      orderIds: ['ORD-2025-002'],
      customerName: 'Jane Smith',
      customerEmail: 'jane.smith@example.com',
      customerPhone: '+1 (555) 234-5678',
      amount: 15.0,
      tax: 1.5,
      total: 16.5,
      status: 'pending',
      paymentMethod: 'PayPal',
      issueDate: '2025-01-15',
      dueDate: '2025-01-30',
      paidDate: null,
      services: [{ name: 'Network Unlock', quantity: 1, price: 15.0 }],
    },
    {
      id: 'INV-2025-003',
      orderIds: ['ORD-2025-003'],
      customerName: 'Mike Johnson',
      customerEmail: 'mike.johnson@example.com',
      customerPhone: '+1 (555) 345-6789',
      amount: 2.5,
      tax: 0.25,
      total: 2.75,
      status: 'overdue',
      paymentMethod: 'Credit Card',
      issueDate: '2025-01-10',
      dueDate: '2025-01-25',
      paidDate: null,
      services: [{ name: 'IMEI Check', quantity: 1, price: 2.5 }],
    },
    {
      id: 'INV-2025-004',
      orderIds: ['ORD-2025-004'],
      customerName: 'Sarah Wilson',
      customerEmail: 'sarah.wilson@example.com',
      customerPhone: '+1 (555) 456-7890',
      amount: 5.0,
      tax: 0.5,
      total: 5.5,
      status: 'paid',
      paymentMethod: 'Bank Transfer',
      issueDate: '2025-01-14',
      dueDate: '2025-01-29',
      paidDate: '2025-01-14',
      services: [{ name: 'Carrier Check', quantity: 1, price: 5.0 }],
    },
    {
      id: 'INV-2025-005',
      orderIds: ['ORD-2025-005'],
      customerName: 'David Brown',
      customerEmail: 'david.brown@example.com',
      customerPhone: '+1 (555) 567-8901',
      amount: 2.5,
      tax: 0.25,
      total: 2.75,
      status: 'paid',
      paymentMethod: 'Credit Card',
      issueDate: '2025-01-13',
      dueDate: '2025-01-28',
      paidDate: '2025-01-13',
      services: [{ name: 'IMEI Check', quantity: 1, price: 2.5 }],
    },
    {
      id: 'INV-2025-006',
      orderIds: ['ORD-2025-006'],
      customerName: 'Emily Davis',
      customerEmail: 'emily.davis@example.com',
      customerPhone: '+1 (555) 678-9012',
      amount: 12.0,
      tax: 1.2,
      total: 13.2,
      status: 'paid',
      paymentMethod: 'PayPal',
      issueDate: '2025-01-13',
      dueDate: '2025-01-28',
      paidDate: '2025-01-13',
      services: [{ name: 'Network Unlock', quantity: 1, price: 12.0 }],
    },
    {
      id: 'INV-2025-007',
      orderIds: ['ORD-2025-007'],
      customerName: 'Robert Miller',
      customerEmail: 'robert.miller@example.com',
      customerPhone: '+1 (555) 789-0123',
      amount: 5.0,
      tax: 0.5,
      total: 5.5,
      status: 'pending',
      paymentMethod: 'Credit Card',
      issueDate: '2025-01-12',
      dueDate: '2025-01-27',
      paidDate: null,
      services: [{ name: 'Carrier Check', quantity: 1, price: 5.0 }],
    },
  ];

  // Calculate totals for summary cards
  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter(
    (invoice) => invoice.status === 'paid',
  ).length;
  const pendingInvoices = invoices.filter(
    (invoice) => invoice.status === 'pending',
  ).length;
  const overdueInvoices = invoices.filter(
    (invoice) => invoice.status === 'overdue',
  ).length;
  const paidRevenue = invoices
    .filter((invoice) => invoice.status === 'paid')
    .reduce((sum, invoice) => sum + invoice.total, 0);

  // Define table columns
  const columns = [
    {
      id: 'id',
      label: 'Invoice ID',
      sortable: true,
      width: '15%',
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {value}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {item.issueDate}
          </div>
        </div>
      ),
    },
    {
      id: 'customerName',
      label: 'Customer',
      sortable: true,
      width: '20%',
      render: (value, item) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
              {value
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {value}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {item.customerEmail}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'services',
      label: 'Services',
      sortable: false,
      width: '20%',
      render: (value) => (
        <div>
          {value.map((service, index) => (
            <div
              key={index}
              className="text-sm text-gray-900 dark:text-gray-100"
            >
              {service.name} (×{service.quantity})
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'amount',
      label: 'Amount',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">
          ${value.toFixed(2)}
        </span>
      ),
    },
    {
      id: 'total',
      label: 'Total',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          ${value.toFixed(2)}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => {
        const statusConfig = {
          paid: {
            bg: 'bg-success-100 dark:bg-success-900/30',
            text: 'text-success-700 dark:text-success-400',
            icon: '●',
          },
          pending: {
            bg: 'bg-warning-100 dark:bg-warning-900/30',
            text: 'text-warning-700 dark:text-warning-400',
            icon: '●',
          },
          overdue: {
            bg: 'bg-error-100 dark:bg-error-900/30',
            text: 'text-error-700 dark:text-error-400',
            icon: '●',
          },
          cancelled: {
            bg: 'bg-gray-100 dark:bg-gray-800',
            text: 'text-gray-700 dark:text-gray-400',
            icon: '●',
          },
        };

        const config = statusConfig[value] || statusConfig.pending;

        return (
          <span
            className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            <span>{config.icon}</span>
            <span className="capitalize">{value}</span>
          </span>
        );
      },
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      sortable: true,
      width: '9%',
      render: (value, item) => (
        <div>
          <div className="text-sm text-gray-900 dark:text-gray-100">
            {value}
          </div>
          {item.paidDate && (
            <div className="text-xs text-success-600 dark:text-success-400">
              Paid: {item.paidDate}
            </div>
          )}
        </div>
      ),
    },
  ];

  // Define table actions
  const actions = [
    {
      type: 'view',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      tooltip: 'View Details',
      variant: 'primary',
    },
    {
      type: 'download',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      tooltip: 'Download Invoice',
    },
  ];

  // Define filters
  const filters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'paid', label: 'Paid' },
        { value: 'pending', label: 'Pending' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      options: [
        { value: 'Credit Card', label: 'Credit Card' },
        { value: 'PayPal', label: 'PayPal' },
        { value: 'Bank Transfer', label: 'Bank Transfer' },
      ],
    },
  ];

  // Action handlers
  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleDownload = (invoice) => {
    handleDownloadInvoice(invoice);
  };

  const handleDownloadInvoice = (invoice) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DeviceLink Global Pvt. Ltd.', 20, 20);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Professional IMEI Services', 20, 30);
    doc.text('INVOICE', 20, 45);

    // Invoice Details
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Details:', 20, 65);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice Number: ${invoice.id}`, 20, 75);
    doc.text(`Issue Date: ${invoice.issueDate}`, 20, 85);
    doc.text(`Due Date: ${invoice.dueDate}`, 20, 95);
    doc.text(
      `Status: ${invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}`,
      20,
      105,
    );

    // Customer Details
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, 125);
    doc.setFont('helvetica', 'normal');
    doc.text(`${invoice.customerName}`, 20, 135);
    doc.text(`${invoice.customerEmail}`, 20, 145);
    doc.text(`${invoice.customerPhone}`, 20, 155);

    // Services Table
    doc.setFont('helvetica', 'bold');
    doc.text('Services:', 20, 175);

    let yPos = 185;
    doc.setFont('helvetica', 'normal');
    doc.text('Service', 20, yPos);
    doc.text('Qty', 100, yPos);
    doc.text('Price', 130, yPos);
    doc.text('Total', 160, yPos);

    yPos += 10;
    doc.line(20, yPos, 190, yPos); // Line under headers
    yPos += 10;

    invoice.services.forEach((service) => {
      doc.text(service.name, 20, yPos);
      doc.text(service.quantity.toString(), 100, yPos);
      doc.text(`$${service.price.toFixed(2)}`, 130, yPos);
      doc.text(`$${(service.quantity * service.price).toFixed(2)}`, 160, yPos);
      yPos += 10;
    });

    yPos += 10;
    doc.line(20, yPos, 190, yPos); // Line before totals
    yPos += 10;

    // Totals
    doc.text(`Subtotal: $${invoice.amount.toFixed(2)}`, 130, yPos);
    yPos += 10;
    doc.text(`Tax: $${invoice.tax.toFixed(2)}`, 130, yPos);
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: $${invoice.total.toFixed(2)}`, 130, yPos);

    // Payment Info
    if (invoice.status === 'paid') {
      yPos += 20;
      doc.setFont('helvetica', 'normal');
      doc.text(`Payment Method: ${invoice.paymentMethod}`, 20, yPos);
      yPos += 10;
      doc.text(`Paid Date: ${invoice.paidDate}`, 20, yPos);
    }

    // Footer
    yPos += 30;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for your business!', 20, yPos);
    yPos += 10;
    doc.text(
      'This invoice was generated electronically by DeviceLink Global Pvt. Ltd.',
      20,
      yPos,
    );

    // Save
    doc.save(`admin-invoice-${invoice.id}.pdf`);
  };

  return (
    <AdminDashboardLayout pageName="Invoices Management">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            View and manage all system invoices with comprehensive billing
            analytics
          </p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Invoices
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {totalInvoices}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  System-wide invoices
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-success-600 dark:text-success-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Paid Revenue
                </p>
                <p className="text-2xl font-bold text-success-600 dark:text-success-400">
                  ${paidRevenue.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {paidInvoices} paid invoices
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-warning-600 dark:text-warning-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending Invoices
                </p>
                <p className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                  {pendingInvoices}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Awaiting payment
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-error-100 dark:bg-error-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-error-600 dark:text-error-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Overdue Invoices
                </p>
                <p className="text-2xl font-bold text-error-600 dark:text-error-400">
                  {overdueInvoices}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Require attention
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* DataTable Component */}
        <DataTable
          title="System Invoices Management"
          columns={columns}
          data={invoices}
          actions={actions}
          onView={handleView}
          onDownload={handleDownload}
          onRefresh={refreshData}
          searchPlaceholder="Search invoices, customers, services..."
          filters={filters}
          rowsPerPageOptions={[5, 10, 20, 50]}
          defaultRowsPerPage={10}
          exportEnabled={true}
          refreshEnabled={true}
          isLoading={isRefreshing}
          className="mt-6"
        />
      </div>

      {/* Invoice View Modal */}
      <AnimatePresence>
        {selectedInvoice && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => setSelectedInvoice(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-4xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">
                      Invoice Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {selectedInvoice.id}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Invoice Info */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Invoice Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Invoice Number
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.id}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Issue Date
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.issueDate}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Due Date
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.dueDate}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Status
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              selectedInvoice.status === 'paid'
                                ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                : selectedInvoice.status === 'pending'
                                  ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400'
                                  : selectedInvoice.status === 'overdue'
                                    ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {selectedInvoice.status.charAt(0).toUpperCase() +
                              selectedInvoice.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Customer Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Customer Details
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Name
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.customerName}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Email
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.customerEmail}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Phone
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.customerPhone}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Payment Method
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedInvoice.paymentMethod}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Payment Details
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Subtotal
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            ${selectedInvoice.amount.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Tax
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            ${selectedInvoice.tax.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Total
                          </p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            ${selectedInvoice.total.toFixed(2)}
                          </p>
                        </div>
                        {selectedInvoice.paidDate && (
                          <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              Paid Date
                            </p>
                            <p className="text-sm text-success-600 dark:text-success-400">
                              {selectedInvoice.paidDate}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Services */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Services
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                      <table className="min-w-full">
                        <thead className="bg-gray-100 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Service
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Quantity
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Price
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {selectedInvoice.services.map((service, index) => (
                            <tr key={index}>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                {service.name}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-center">
                                {service.quantity}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
                                ${service.price.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">
                                ${(service.quantity * service.price).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Related Orders */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Related Orders
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedInvoice.orderIds.map((orderId, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                        >
                          {orderId}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                  <button
                    onClick={() => handleDownloadInvoice(selectedInvoice)}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Download Invoice
                  </button>
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminDashboardLayout>
  );
};

export default AdminInvoicesPage;
